// UploadProgressOverlay — app-root progress chip for ALL file uploads.
//
// WHY THIS EXISTS (2026-10-01): every upload in the app funnels through
// uploadWithValidation() (src/utils/upload.ts), which now publishes byte-level
// progress to src/utils/uploadProgressBus.ts. Before that, large uploads (e.g.
// 50-100 MB chat videos) showed only a spinner inside the sending view — users
// reasonably assumed the app froze. This overlay renders one compact chip per
// active upload in the viewport corner; individual views may STILL show their
// own progress bars (mild duplication accepted; the chip is small and
// non-blocking).
//
// TEARDOWN DISCIPLINE (AGENTS.md §2): subscribes in useEffect and
// unsubscribes in cleanup. Renders nothing when no uploads are active.

import { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
    subscribeUploadProgress,
    getActiveUploads,
    type UploadProgressEvent,
} from '../utils/uploadProgressBus';

export default function UploadProgressOverlay() {
    const { t } = useTranslation();
    const [uploads, setUploads] = useState<UploadProgressEvent[]>(getActiveUploads());

    useEffect(() => {
        const unsubscribe = subscribeUploadProgress(() => {
            setUploads(getActiveUploads());
        });
        return unsubscribe;
    }, []);

    if (uploads.length === 0) return null;

    return (
        <div className="fixed bottom-4 right-4 z-60 flex flex-col gap-2 pointer-events-none">
            {uploads.map(u => {
                const indeterminate = u.status === 'uploading' && u.percent < 0;
                const done = u.status === 'done';
                const failed = u.status === 'error';
                return (
                    <div
                        key={u.id}
                        className="flex items-center gap-2.5 min-w-[220px] max-w-[320px] rounded-xl border border-border bg-surface px-3 py-2 shadow-md"
                    >
                        {failed ? (
                            <XCircle size={16} className="shrink-0 text-red-500" />
                        ) : done ? (
                            <CheckCircle2 size={16} className="shrink-0 text-accent" />
                        ) : (
                            <Loader2 size={16} className="shrink-0 text-accent animate-spin" />
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold truncate text-fg">{u.label}</p>
                            {failed ? (
                                <p className="text-[10px] font-semibold text-red-500">{t('uploadProgress.failed')}</p>
                            ) : done ? (
                                <p className="text-[10px] font-semibold text-muted">{t('uploadProgress.done')}</p>
                            ) : indeterminate ? (
                                <p className="text-[10px] font-semibold text-muted">{t('uploadProgress.preparing')}</p>
                            ) : (
                                <div className="w-full h-1 mt-1 rounded-full bg-border overflow-hidden">
                                    <div
                                        className="h-full bg-accent rounded-full transition-all duration-150"
                                        style={{ width: `${u.percent}%` }}
                                    />
                                </div>
                            )}
                        </div>
                        {!failed && !done && !indeterminate && (
                            <span className="text-[10px] font-bold tabular-nums text-muted shrink-0">
                                {u.percent}%
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
